import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Към началната страница
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center mb-4">
            <ShieldCheckIcon className="w-12 h-12 text-primary-600 mr-4" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Политика за поверителност</h1>
              <p className="text-gray-600 mt-2">Последна актуализация: {new Date().toLocaleDateString('bg-BG', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Въведение</h2>
            <p className="text-gray-700 leading-relaxed">
              Добре дошли в CarCube („ние", „нашият" или „нас"). Ние сме ангажирани да защитаваме вашата лична 
              информация и правото ви на поверителност. Настоящата Политика за поверителност обяснява как събираме, 
              използваме, разкриваме и защитаваме вашата информация, когато посещавате нашия уебсайт и използвате 
              нашите услуги.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              Моля, прочетете внимателно тази политика за поверителност. Ако не сте съгласни с условията, 
              моля не използвайте сайта.
            </p>
          </section>

          {/* Data Controller */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Администратор на лични данни</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Администратор на личните данни по смисъла на Регламент (ЕС) 2016/679 (GDPR) и Закона за защита на 
              личните данни е:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <ul className="space-y-2 text-gray-700">
                <li><strong>Наименование:</strong> CarCube</li>
                <li><strong>Адрес:</strong> България</li>
                <li><strong>Имейл:</strong> carcube.bg@gmail.com</li>
              </ul>
            </div>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Информация, която събираме</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.1 Лична информация</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Събираме лична информация, която доброволно ни предоставяте, когато:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Се свържете с нас чрез формата за контакт</li>
              <li>Направите запитване за автомобил</li>
              <li>Се абонирате за нашия бюлетин</li>
              <li>Регистрирате акаунт</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              Тази информация може да включва:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Име и фамилия</li>
              <li>Имейл адрес</li>
              <li>Телефонен номер</li>
              <li>Съдържание на съобщения</li>
              <li>Предпочитания за автомобили и запитвания</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.2 Автоматично събирана информация</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Когато посещавате нашия уебсайт, автоматично събираме определена информация за вашето устройство:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>IP адрес</li>
              <li>Тип и версия на браузъра</li>
              <li>Операционна система</li>
              <li>Посетени страници и време на престой</li>
              <li>Информация за устройството</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.3 Бисквитки и технологии за проследяване</h3>
            <p className="text-gray-700 leading-relaxed">
              Използваме бисквитки и подобни технологии за проследяване на активността на нашия уебсайт. 
              Бисквитките са малки файлове с данни, съхранявани на вашето устройство. Можете да настроите 
              браузъра си да отказва всички бисквитки.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-3">
              <p className="text-blue-800">
                <strong>Видове бисквитки, които използваме:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 text-blue-700 ml-4 mt-2">
                <li><strong>Задължителни бисквитки:</strong> Необходими за функционирането на уебсайта</li>
                <li><strong>Функционални бисквитки:</strong> Запомнят вашите предпочитания и настройки</li>
              </ul>
            </div>
          </section>

          {/* Legal Basis */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Правно основание за обработка</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Обработваме вашите лични данни въз основа на следните правни основания съгласно чл. 6 от GDPR:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Съгласие (чл. 6, пар. 1, буква „а"):</strong> Когато сте дали съгласие за конкретни цели</li>
              <li><strong>Изпълнение на договор (чл. 6, пар. 1, буква „б"):</strong> Когато обработката е необходима за изпълнение на договор</li>
              <li><strong>Законово задължение (чл. 6, пар. 1, буква „в"):</strong> Когато обработката е необходима за спазване на законови задължения</li>
              <li><strong>Легитимен интерес (чл. 6, пар. 1, буква „е"):</strong> Когато обработката е необходима за нашите легитимни интереси</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Как използваме вашата информация</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Използваме събраната информация за следните цели:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>За да отговорим на вашите запитвания и да предоставим клиентска поддръжка</li>
              <li>За да ви изпратим информация за автомобили, от които се интересувате</li>
              <li>За да подобрим нашия уебсайт и услуги</li>
              <li>За анализ на използването на уебсайта</li>
              <li>За предотвратяване на измами и подобряване на сигурността</li>
              <li>За спазване на законови задължения</li>
            </ul>
          </section>

          {/* Sharing Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Споделяне на вашата информация</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Можем да споделяме вашата информация в следните случаи:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>С доставчици на услуги:</strong> Можем да споделяме информация с трети страни, 
              които извършват услуги от наше име (например хостинг, имейл доставка)</li>
              <li><strong>По законови причини:</strong> Можем да разкрием информация, ако това се изисква от закона 
              или в отговор на валидни искания от публични органи</li>
              <li><strong>С ваше съгласие:</strong> Можем да разкрием личната ви информация за всякакви други цели 
              с вашето съгласие</li>
            </ul>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
              <p className="text-green-800">
                <strong>Важно:</strong> Ние НЕ продаваме вашата лична информация на трети страни.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Срок на съхранение на данните</h2>
            <p className="text-gray-700 leading-relaxed">
              Съхраняваме вашата лична информация само докато е необходимо за целите, описани в тази Политика 
              за поверителност, освен ако по-дълъг срок на съхранение не се изисква или разрешава от закона. 
              Когато вече не се нуждаем от вашата лична информация, ще я изтрием или анонимизираме по сигурен начин.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              Обикновено съхраняваме данни за следните периоди:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mt-2">
              <li>Данни от контактна форма: до 3 години след последния контакт</li>
              <li>Данни за транзакции: 10 години съгласно данъчното законодателство</li>
              <li>Автоматично събрани данни: до 1 година</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Сигурност на данните</h2>
            <p className="text-gray-700 leading-relaxed">
              Прилагаме подходящи технически и организационни мерки за сигурност, за да защитим вашата лична 
              информация срещу неоторизиран достъп, промяна, разкриване или унищожаване. Тези мерки включват:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mt-3">
              <li>SSL криптиране на връзката</li>
              <li>Редовни актуализации на системите за сигурност</li>
              <li>Ограничен достъп до лични данни само за оторизирани лица</li>
            </ul>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Връзки към трети страни</h2>
            <p className="text-gray-700 leading-relaxed">
              Нашият уебсайт може да съдържа връзки към уебсайтове на трети страни. Не носим отговорност за 
              практиките за поверителност или съдържанието на тези външни сайтове. Препоръчваме ви да прегледате 
              политиките за поверителност на всички сайтове на трети страни, които посещавате.
            </p>
          </section>

          {/* Supervisory Authority */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Надзорен орган</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Ако считате, че обработването на вашите лични данни нарушава GDPR или Закона за защита на личните 
              данни, имате право да подадете жалба до:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">Комисия за защита на личните данни</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Адрес:</strong> бул. „Проф. Цветан Лазаров" № 2, София 1592</li>
                <li><strong>Телефон:</strong> 02/915 35 18</li>
                <li><strong>Имейл:</strong> kzld@cpdp.bg</li>
                <li><strong>Уебсайт:</strong> www.cpdp.bg</li>
              </ul>
            </div>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Промени в тази политика</h2>
            <p className="text-gray-700 leading-relaxed">
              Можем да актуализираме тази Политика за поверителност периодично. Ще ви уведомим за всички 
              промени, като публикуваме новата Политика за поверителност на тази страница и актуализираме 
              датата „Последна актуализация". Препоръчваме ви да преглеждате тази Политика за поверителност 
              периодично.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Контакти</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Ако имате въпроси относно тази Политика за поверителност или нашите практики за защита на данните, 
              моля свържете се с нас:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">CarCube</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Имейл:</strong> carcube.bg@gmail.com</li>
                <li><strong>Уебсайт:</strong> <Link to="/contact" className="text-primary-600 hover:text-primary-700">Форма за контакт</Link></li>
              </ul>
            </div>
          </section>

          {/* GDPR Notice */}
          <section className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <h2 className="text-xl font-bold text-blue-900 mb-3">Съответствие с GDPR</h2>
            <p className="text-blue-800 leading-relaxed">
              Тази Политика за поверителност е изготвена в съответствие с Регламент (ЕС) 2016/679 (Общ регламент 
              относно защитата на данните - GDPR) и Закона за защита на личните данни на Република България. 
              Стремим се да осигурим пълна прозрачност относно обработването на вашите лични данни.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Към началната страница
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
